/**
 * @file Leads export page.
 * @description Manages lead export creation, lock duration options, preview table, and CSV download.
 */
import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { format, parseISO } from 'date-fns';
import { Download, Calendar, Filter, Columns3, Check } from 'lucide-react';
import { cn } from '../../utils/cn';
import FilterBar from '../../components/FilterBar';
import { Lead } from '../../services/apiService';
import { ExportSettingsService } from '../../services/exportSettingsService';
import { LeadsService, ExportFilterResult } from '../../services/leadsService';
import { LeadsExportService } from '../../services/leadsExportService';
import { PageHeader } from '../../components/ui';
import { formatCount } from '../../utils/formatNumbers';

interface LockDurationOption {
  value: number;
  label: string;
}

const AVAILABLE_COLUMNS = [
  { key: 'name', label: 'Name' },
  { key: 'company_name', label: 'Company Name' },
  { key: 'phone', label: 'Mobile' },
  { key: 'email', label: 'Email' },
  { key: 'dnd_status', label: 'DND Status' },
  { key: 'age', label: 'Age' },
  { key: 'city', label: 'City' },
  { key: 'plan', label: 'Plan' },
  { key: 'income', label: 'Income' },
  { key: 'vehicle_brand_name', label: 'Vehicle Brand Name' },
  { key: 'source', label: 'Source' },
  { key: 'data_type', label: 'Data Type' },
  { key: 'created_at', label: 'Upload Date' },
] as const;

const MANDATORY_EXPORT_COLUMN = 'phone';

const PREVIEW_ROWS = 10;

function formatUploadDate(dateString: string | null | undefined): string {
  if (!dateString) return '—';
  try {
    const normalized = dateString.includes('T') ? dateString : dateString.replace(' ', 'T');
    return format(parseISO(normalized), 'MMM dd, yyyy');
  } catch {
    return dateString;
  }
}

function filterLeadsByRange(
  leads: Lead[],
  filters: Record<string, string>,
): Lead[] {
  let filteredLeads = leads;

  const minAge = filters['min_age'] ? Number.parseInt(filters['min_age'], 10) : null;
  const maxAge = filters['max_age'] ? Number.parseInt(filters['max_age'], 10) : null;
  if (minAge !== null || maxAge !== null) {
    filteredLeads = filteredLeads.filter((lead) => {
      if (lead.age == null) return false;
      if (minAge !== null && lead.age < minAge) return false;
      if (maxAge !== null && lead.age > maxAge) return false;
      return true;
    });
  }

  const minIncome = filters['min_income'] ? Number.parseInt(filters['min_income'], 10) : null;
  const maxIncome = filters['max_income'] ? Number.parseInt(filters['max_income'], 10) : null;
  if (minIncome !== null || maxIncome !== null) {
    filteredLeads = filteredLeads.filter((lead) => {
      if (lead.income == null) return false;
      if (minIncome !== null && lead.income < minIncome) return false;
      if (maxIncome !== null && lead.income > maxIncome) return false;
      return true;
    });
  }

  return filteredLeads;
}

const LeadsExport: React.FC = () => {
  const [leads, setLeads] = useState<Lead[]>([]);
  const [loading, setLoading] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [lockDurationDays, setLockDurationDays] = useState(10);
  const [lockDurationOptions, setLockDurationOptions] = useState<LockDurationOption[]>([]);
  const [exportResult, setExportResult] = useState<ExportFilterResult | null>(null);
  const [errorMessage, setErrorMessage] = useState('');
  const [lastFilters, setLastFilters] = useState<Record<string, string>>({});
  const [selectedColumns, setSelectedColumns] = useState<string[]>(AVAILABLE_COLUMNS.map(c => c.key));

  const toggleColumn = useCallback((col: string) => {
    if (col === MANDATORY_EXPORT_COLUMN) return;
    setSelectedColumns(prev => {
      if (prev.includes(col)) {
        if (prev.length === 1) return prev;
        return prev.filter(c => c !== col);
      }
      return [...prev, col];
    });
  }, []);

  const selectAllColumns = useCallback(() => {
    setSelectedColumns(AVAILABLE_COLUMNS.map(c => c.key));
  }, []);

  useEffect(() => {
    const options = ExportSettingsService.getLockDurationOptions().map(day => ({
      value: day.value,
      label: day.label,
    }));
    setLockDurationOptions(options);
    if (options.length > 0) {
      setLockDurationDays(options[0].value);
    }
  }, []);

  const handleFiltersChange = async (filters: Record<string, string>) => {
    setLoading(true);
    setErrorMessage('');
    setLastFilters(filters);
    try {
      const result = await LeadsService.filterLeadsForExport({
        advertiser_id: filters['advertiser_id'] || '',
        campaign_id: filters['campaign_id'] || '',
        source: filters['source'] || '',
        from_date: filters['date_from'] || '',
        to_date: filters['date_to'] || '',
        data_type: filters['data_type'] || '',
        export_log_days: lockDurationDays.toString(),
        min_age: filters['min_age'] || '',
        max_age: filters['max_age'] || '',
        min_income: filters['min_income'] || '',
        max_income: filters['max_income'] || '',
        company_name: filters['company_name'] || '',
        vehicle_brand_name: filters['vehicle_brand_name'] || '',
        dnd_status: filters['dnd_status'] || '',
      });

      if (result.status === 'error') {
        throw new Error(result.message || 'Failed to fetch leads for export.');
      }

      const filteredLeads = filterLeadsByRange(result.leads, filters);

      setLeads(filteredLeads);
      setExportResult(result);
    } catch (error) {
      console.error('Error fetching leads:', error);
      setErrorMessage(error instanceof Error ? error.message : 'Failed to fetch leads. Please try again.');
      setLeads([]);
      setExportResult(null);
    } finally {
      setLoading(false);
    }
  };

  const generateFileName = () => {
    let advertiser = 'Advertiser';
    let campaign = 'Campaign';
    let dataType = 'lead';
    if (leads.length > 0) {
      advertiser = leads[0].advertiser_name || advertiser;
      campaign = leads[0].campaign_name || campaign;
    }
    dataType = lastFilters['data_type'] || dataType;
    const date = new Date().toISOString().slice(0, 10).replace(/-/g, '');
    const sanitize = (value: string) => value.replace(/[^a-zA-Z0-9_-]/g, '_');
    return `${sanitize(advertiser)}_${sanitize(campaign)}_${sanitize(dataType)}_${date}.csv`;
  };

  const canDownload =
    Boolean(exportResult?.exportUrl) ||
    Boolean(exportResult?.exportMeta?.file_name) ||
    Boolean(exportResult?.exportMeta?.download_url);

  const handleExport = async (event?: React.MouseEvent<HTMLButtonElement>) => {
    event?.preventDefault();
    event?.stopPropagation();
    setErrorMessage('');
    if (!canDownload) {
      setErrorMessage('No export URL found. Please apply filters first.');
      return;
    }
    setDownloading(true);
    try {
      let blob: Blob;
      if (exportResult?.exportUrl) {
        blob = await LeadsExportService.downloadFromExportUrl(exportResult.exportUrl);
      } else if (exportResult?.exportMeta?.file_name) {
        blob = await LeadsExportService.downloadExportedLeadsFile(exportResult.exportMeta.file_name);
      } else if (exportResult?.exportMeta?.download_url) {
        blob = await LeadsExportService.downloadFromExportUrl(exportResult.exportMeta.download_url);
      } else {
        throw new Error('No download source available.');
      }

      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = generateFileName();
      a.style.display = 'none';
      a.rel = 'noopener noreferrer';
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.setTimeout(() => {
        window.URL.revokeObjectURL(url);
        window.location.reload();
      }, 100);
    } catch (error) {
      console.error('Download error:', error);
      setErrorMessage(error instanceof Error ? error.message : 'Failed to download exported file.');
    } finally {
      setDownloading(false);
    }
  };

  const selectedColumnSet = useMemo(() => new Set(selectedColumns), [selectedColumns]);
  const previewLeads = useMemo(() => leads.slice(0, PREVIEW_ROWS), [leads]);

  const totalRecords = exportResult?.pagination.total ?? leads.length;
  const totalPages = exportResult?.pagination.total_page ?? 1;

  const renderCell = (lead: Lead, key: string) => {
    switch (key) {
      case 'name':
        return lead.name || '—';
      case 'company_name':
        return lead.company_name || '—';
      case 'phone':
        return lead.phone || '—';
      case 'email':
        return lead.email || '—';
      case 'dnd_status':
        return lead.dnd_status || '—';
      case 'age':
        return lead.age != null ? String(lead.age) : 'N/A';
      case 'city':
        return lead.city || '—';
      case 'plan':
        return lead.plan || '—';
      case 'income':
        return lead.income != null ? `₹${lead.income.toLocaleString('en-IN')}` : '—';
      case 'vehicle_brand_name':
        return lead.vehicle_brand_name || '—';
      case 'source':
        return lead.source_name || lead.source || '—';
      case 'data_type':
        return lead.data_type || '—';
      case 'created_at':
        return formatUploadDate(lead.created_at);
      default:
        return '—';
    }
  };

  return (
    <div className="space-y-6">
      <PageHeader
        title="Export Leads"
        subtitle="Download filtered lead data as CSV"
        subtitleClassName="text-secondary-100 text-sm"
      />

      {errorMessage && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-sm text-red-700">
          {errorMessage}
        </div>
      )}

      <div className="bg-white rounded-lg border border-gray-200 p-4 sm:p-6">
        <div className="mb-4 flex flex-col gap-3 border-b border-gray-200 pb-4 sm:flex-row sm:items-center sm:justify-between">
          <h3 className="text-lg font-semibold text-gray-900">Export Configuration</h3>
          {/* <div className="flex flex-wrap items-center gap-2">
            <div className="flex min-w-0 flex-wrap items-center gap-2">
              <Calendar size={16} className="shrink-0 text-gray-500" />
              <label className="text-sm font-medium text-gray-700 shrink-0">Lock Duration:</label>
              <select
                value={lockDurationDays}
                onChange={(e) => setLockDurationDays(Number.parseInt(e.target.value, 10))}
                className="px-3 py-1 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
                disabled={lockDurationOptions.length === 0}
              >
                {lockDurationOptions.length === 0 ? (
                  <option value="">Loading...</option>
                ) : (
                  lockDurationOptions.map(opt => (
                    <option key={opt.value} value={opt.value}>
                      {opt.label}
                    </option>
                  ))
                )}
              </select>
            </div>
          </div> */}
        </div>

        <div className="mb-5 rounded-xl border border-gray-200 bg-gradient-to-br from-gray-50 to-white p-4 sm:p-5">
          <div className="mb-4 flex flex-col gap-3 border-b border-gray-100 pb-4 sm:flex-row sm:items-center sm:justify-between">
            <div className="flex items-start gap-3">
              <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-primary-100">
                <Columns3 size={20} className="text-primary-600" aria-hidden />
              </div>
              <div>
                <h4 className="text-sm font-semibold text-gray-900">Select columns to export</h4>
                <p className="mt-0.5 text-xs text-gray-500">Choose which fields appear in your CSV file</p>
                <span className="mt-2 inline-flex items-center rounded-full bg-primary-50 px-2.5 py-0.5 text-xs font-medium text-primary-700 ring-1 ring-primary-200/80">
                  {selectedColumns.length} of {AVAILABLE_COLUMNS.length} selected
                </span>
              </div>
            </div>
            <button
              type="button"
              onClick={selectAllColumns}
              disabled={selectedColumns.length === AVAILABLE_COLUMNS.length}
              className="shrink-0 self-start rounded-lg border border-gray-200 bg-white px-3 py-1.5 text-xs font-medium text-gray-700 shadow-sm transition hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50 sm:self-center"
            >
              Select all
            </button>
          </div>

          <div className="grid grid-cols-2 gap-2 min-[400px]:grid-cols-2 sm:grid-cols-3 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-6">
            {AVAILABLE_COLUMNS.map(col => {
              const isSelected = selectedColumns.includes(col.key);
              const isMandatory = col.key === MANDATORY_EXPORT_COLUMN;
              const isLastSelected = isSelected && selectedColumns.length === 1;
              const isDisabled = isMandatory ? isSelected : isLastSelected;
              return (
                <button
                  key={col.key}
                  type="button"
                  onClick={() => toggleColumn(col.key)}
                  disabled={isDisabled}
                  aria-pressed={isSelected}
                  className={cn(
                    'flex w-full cursor-pointer min-w-0 items-center gap-2.5 rounded-lg border px-2 py-2 text-left text-sm font-medium transition-all',
                    isSelected
                      ? 'border-primary-500 bg-primary-50 text-primary-900 shadow-sm ring-1 ring-primary-500/25'
                      : 'border-gray-200 bg-white text-gray-700 hover:border-primary-300 hover:bg-gray-50',
                    isDisabled && 'cursor-not-allowed opacity-90',
                  )}
                >
                  <span
                    className={cn(
                      'flex h-5 w-5 shrink-0 items-center justify-center rounded border transition-colors',
                      isSelected ? 'border-primary-600 bg-primary-600 text-white' : 'border-gray-300 bg-white',
                    )}
                    aria-hidden
                  >
                    {isSelected ? <Check size={12} strokeWidth={3} /> : null}
                  </span>
                  <span className="min-w-0 text-xs leading-snug">{col.label}</span>
                </button>
              );
            })}
          </div>
        </div>

        <ul className="space-y-2 text-sm text-gray-600 break-words">
          <li>• Apply filters below to preview leads and get the export download link</li>
          <li>
            • File naming format:{' '}
            <code className="mt-0.5 inline-block max-w-full break-all rounded bg-gray-100 px-1.5 py-0.5 text-xs text-gray-800">
              Advertiser_Campaign_DataType_YYYYMMDD.csv
            </code>
          </li>
          {/* <li>• Current lock duration: <strong>{lockDurationDays} days</strong></li> */}
        </ul>
      </div>

      <FilterBar
        onFiltersChange={handleFiltersChange}
        showDataType={true}
        gridClassName="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-2"
      />

      {loading && (
        <div className="flex items-center justify-center rounded-lg border border-gray-200 bg-white py-12">
          <div className="h-8 w-8 animate-spin rounded-full border-2 border-primary-600 border-t-transparent" />
          <span className="ml-3 text-sm text-gray-600">Loading export preview…</span>
        </div>
      )}

      {!loading && exportResult && canDownload && (
        <div className="bg-green-50 border border-green-200 rounded-lg p-4">
          <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
            <div className="flex items-start gap-3">
              <Download size={20} className="text-green-600 mt-0.5 shrink-0" />
              <div>
                <h4 className="font-medium text-green-800">Export Ready</h4>
                <p className="text-sm text-green-700">
                  {formatCount(totalRecords)} total records · page {exportResult.pagination.page} of{' '}
                  {formatCount(totalPages)}
                </p>
                {exportResult.exportMeta?.created_at && (
                  <p className="text-xs text-green-600 mt-0.5">
                    Created: {new Date(exportResult.exportMeta.created_at).toLocaleString()}
                  </p>
                )}
              </div>
            </div>
            <button
              type="button"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                void handleExport(e);
              }}
              disabled={downloading}
              className="shrink-0 px-4 py-2 bg-green-600 text-white text-sm font-medium rounded-md hover:bg-green-700 transition-colors disabled:opacity-60">
              {downloading ? 'Downloading…' : 'Download CSV'}
            </button>
          </div>
        </div>
      )}

      {!loading && (
        <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-200 flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <h3 className="text-lg font-semibold text-gray-900">Export Preview</h3>
              <p className="text-sm text-gray-500 mt-0.5">
                Showing {previewLeads.length} of {formatCount(totalRecords)} matching records
              </p>
            </div>
            {/* {canDownload && (
              <button
                type="button"
                onClick={() => void handleExport()}
                disabled={downloading}
                className="px-4 py-2 text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 rounded-md transition-colors disabled:opacity-60"
              >
                {downloading ? 'Downloading…' : 'Download CSV'}
              </button>
            )} */}
          </div>

          <div className="overflow-x-auto">
            {previewLeads.length > 0 ? (
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-gray-500">
                      #
                    </th>
                    {AVAILABLE_COLUMNS.filter(c => selectedColumnSet.has(c.key)).map(c => (
                      <th
                        key={c.key}
                        className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-gray-500 whitespace-nowrap"
                      >
                        {c.label}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100 bg-white">
                  {previewLeads.map((lead, index) => (
                    <tr key={`${lead.id}-${index}`} className="hover:bg-gray-50">
                      <td className="px-4 py-3 text-sm text-gray-500 tabular-nums">{index + 1}</td>
                      {AVAILABLE_COLUMNS.filter(c => selectedColumnSet.has(c.key)).map(c => (
                        <td
                          key={c.key}
                          className="px-4 py-3 text-sm text-gray-900 whitespace-nowrap max-w-[220px] truncate"
                          title={renderCell(lead, c.key)}
                        >
                          {renderCell(lead, c.key)}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : (
              <div className="text-center py-12">
                <Filter size={48} className="mx-auto text-gray-300 mb-4" />
                <p className="text-gray-500">No leads found matching your criteria.</p>
                <p className="text-sm text-gray-400 mt-1">Apply filters above to load preview data.</p>
              </div>
            )}

            {previewLeads.length > 0 && totalRecords > PREVIEW_ROWS && (
              <div className="px-6 py-4 bg-gray-50 text-center text-sm text-gray-500 border-t border-gray-100">
                Preview shows first {PREVIEW_ROWS} rows. Download CSV for all {formatCount(totalRecords)} records.
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default LeadsExport;
